import type { contracts } from '@giftrage/shared-contracts'
import { createORPCClient, createSafeClient, ORPCError } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'
import type { ContractRouterClient } from '@orpc/contract'
import { ClientRetryPlugin } from '@orpc/client/plugins'
import type { ClientRetryPluginContext } from '@orpc/client/plugins'
import { useUser } from '@/stores/user'

interface ORPCClientContext extends ClientRetryPluginContext {}

const link = new RPCLink<ORPCClientContext>({
	url: import.meta.env.VITE_API_URL,
	headers: () => {
		const user = useUser()

		return {
			Authorization: user.token
		}
	},
	plugins: [
		new ClientRetryPlugin({
			default: {
				// Optional override for default options
				retryDelay: (options) => {
					if (
						options.error instanceof ORPCError &&
						options.error.defined &&
						options.error.code === 'UNAUTHORIZED'
					) {
						return 0
					}
					if (
						options.error instanceof ORPCError &&
						options.error.code === 'RATE_LIMITED' &&
						options.error.data?.retryAfter
					) {
						// Use a longer delay suggested by the server, e.g. error.data.retryAfter
						return options.error.data?.retryAfter ?? 5000
					}

					return 2000 // Default delay for all other errors
				},
				shouldRetry: async (options) => {
					if (options.error instanceof ORPCError) {
						if (
							options.error.defined &&
							options.error.code === 'UNAUTHORIZED' &&
							!options.path.includes('auth')
						) {
							const user = useUser()

							await user.auth()

							if (!user.token) {
								return false
							}

							return true
						}
						if (
							options.error.status >= 400 &&
							options.error.status < 500 &&
							options.error.status !== 429
						) {
							return false
						}
						if (
							options.error.defined &&
							options.error.code !== 'RATE_LIMITED'
						) {
							return false
						}
					}

					// Retry on network errors or server errors (5xx)
					return true
				},
				retry: 3
			}
		})
	],
	fetch: (request, init) => {
		return globalThis.fetch(request, {
			...init,
			credentials: 'include'
		})
	}
})

const unsafeClient: ContractRouterClient<typeof contracts, ORPCClientContext> =
	createORPCClient(link)
export const client = createSafeClient(unsafeClient)
